/* ==========================================================================
   DUB-CART.JS  -  how the Down Under Bro cart behaves (every page)
   --------------------------------------------------------------------------
   Works with sections/dub-cart.liquid (the cart's layout) and
   assets/dub-cart.css (its look).

   WHAT IT DOES
     1. Opens and closes the cart (cart icon, round cart button, X button,
        dark background, Escape key).
     2. Keeps every cart count on the page up to date (the header bubble,
        the round button and "Your Cart (n)").
     3. - / + and Remove: tells Shopify, then shows the updated cart
        straight away (no page reload).
     4. After Add to cart on a product page: updates the cart and opens it.
     5. Keeps two older shortcuts working for the Dub sections:
        window.dubOpenCart() and the "dub:cart:added" signal.
   ========================================================================== */

(function () {
  'use strict';

  const cart = document.querySelector('[data-dub-cart]');
  if (!cart) return;

  const backdrop = document.querySelector('[data-dub-cart-backdrop]');
  const sectionId = cart.dataset.sectionId;
  const openAfterAdd = cart.dataset.openAfterAdd === 'true';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  let lastFocused = null;

  /* ------------------------------------------------------------------------
     1. OPEN / CLOSE
     ------------------------------------------------------------------------ */
  function isOpen() {
    return cart.classList.contains('is-open');
  }

  function openCart() {
    if (isOpen()) return;
    lastFocused = document.activeElement;
    cart.classList.add('is-open');
    cart.setAttribute('aria-hidden', 'false');
    backdrop && backdrop.classList.add('is-open');
    document.documentElement.classList.add('dub-cart-open');
    // Move keyboard focus into the cart (onto the X button)
    const closeBtn = cart.querySelector('.dub-cart__close');
    setTimeout(() => (closeBtn || cart).focus({ preventScroll: true }), 50);
  }

  function closeCart() {
    if (!isOpen()) return;
    cart.classList.remove('is-open');
    cart.setAttribute('aria-hidden', 'true');
    backdrop && backdrop.classList.remove('is-open');
    document.documentElement.classList.remove('dub-cart-open');
    // Put keyboard focus back where it was before the cart opened
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus({ preventScroll: true });
    }
  }

  /* ------------------------------------------------------------------------
     2. CART COUNTS
     Updates every [data-dub-cart-count] bubble (hidden when 0) and the
     "Your Cart (n)" text.
     ------------------------------------------------------------------------ */
  function setCount(count, bump) {
    document.querySelectorAll('[data-dub-cart-count]').forEach((el) => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
      if (bump && count > 0) {
        el.classList.remove('dub-cart-bump');
        void el.offsetWidth; // restart the animation
        el.classList.add('dub-cart-bump');
      }
    });
    document.querySelectorAll('[data-dub-cart-count-text]').forEach((el) => {
      el.textContent = count;
    });
  }

  /* ------------------------------------------------------------------------
     3. SHOW THE LATEST CART
     Shopify sends back a fresh copy of the cart section; we swap the
     inside of the cart for it and update the counts.
     ------------------------------------------------------------------------ */
  function showFreshCart(sectionHtml, itemCount, bump) {
    if (sectionHtml) {
      const fresh = new DOMParser()
        .parseFromString(sectionHtml, 'text/html')
        .querySelector('[data-dub-cart-body]');
      const current = cart.querySelector('[data-dub-cart-body]');
      if (fresh && current) {
        const focusWasInside = cart.contains(document.activeElement);
        current.replaceWith(fresh);
        if (focusWasInside && isOpen()) cart.focus({ preventScroll: true });
      }
    }
    if (typeof itemCount === 'number') setCount(itemCount, bump);
  }

  // Ask Shopify for the latest cart (used after Add to cart, and on "back").
  // If we already know the new item count, we only need the cart's layout.
  function refreshCart(bump, knownCount) {
    const countRequest =
      typeof knownCount === 'number'
        ? Promise.resolve({ item_count: knownCount })
        : fetch(root + 'cart.js').then((r) => r.json());
    return Promise.all([
      fetch(root + '?sections=' + encodeURIComponent(sectionId)).then((r) => r.json()),
      countRequest,
    ])
      .then(([sections, data]) => showFreshCart(sections[sectionId], data.item_count, bump))
      .catch(() => {});
  }

  // How many items the cart shows right now.
  function currentCount() {
    const el = cart.querySelector('[data-dub-cart-count-text]');
    return el ? parseInt(el.textContent, 10) || 0 : 0;
  }

  /* ------------------------------------------------------------------------
     4. - / + AND REMOVE
     Each button carries the new quantity (0 = remove). We send it to
     Shopify and get the updated cart back in the same trip.
     ------------------------------------------------------------------------ */
  function changeQuantity(lineKey, quantity) {
    cart.classList.add('is-busy');
    return fetch(root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: quantity, sections: [sectionId] }),
    })
      .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) {
          showError(data.description || data.message || 'Sorry, that didn’t work. Please try again.');
          return;
        }
        showFreshCart(data.sections && data.sections[sectionId], data.item_count, false);
      })
      .catch(() => showError('Sorry, that didn’t work. Please try again.'))
      .finally(() => cart.classList.remove('is-busy'));
  }

  // Shows Shopify's message (e.g. "You can only add 3 of this item") above Subtotal.
  function showError(message) {
    const box = cart.querySelector('[data-dub-cart-error]');
    if (!box) return;
    box.textContent = message;
    box.hidden = false;
  }

  /* ------------------------------------------------------------------------
     5. TAPS AND KEYS
     ------------------------------------------------------------------------ */
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // Any cart icon / cart button on the page
    if (target.closest('[data-dub-open-cart]')) {
      event.preventDefault();
      openCart();
      return;
    }
    // X button or the dark background
    if (target.closest('[data-dub-close-cart]')) {
      closeCart();
      return;
    }
    // - / + / Remove inside the cart
    const qtyButton = target.closest('[data-dub-qty]');
    if (qtyButton && cart.contains(qtyButton)) {
      const line = qtyButton.closest('[data-dub-cart-line]');
      if (!line) return;
      changeQuantity(line.getAttribute('data-dub-cart-line'), Math.max(0, parseInt(qtyButton.getAttribute('data-dub-qty'), 10) || 0));
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!isOpen()) return;

    // Escape closes the cart
    if (event.key === 'Escape') {
      closeCart();
      return;
    }

    // Keep the Tab key inside the cart while it's open
    if (event.key === 'Tab') {
      const focusable = [...cart.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])')].filter(
        (el) => el.offsetParent !== null
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === cart)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  /* ------------------------------------------------------------------------
     6. AFTER ADD TO CART (product pages)
     Shopify's product form sends a "shopify:cart:lines-update" signal.
     - If the cart already has things in it, it slides open straight away
       (slightly faded) and fills in the new item a moment later.
     - If the cart was empty, it waits for Shopify to confirm, then opens.
     - If Shopify says no (e.g. sold out), the cart closes again and the
       product page shows Shopify's message.
     ------------------------------------------------------------------------ */
  document.addEventListener('shopify:cart:lines-update', (event) => {
    const wasAdd = event.action === 'add';
    const openNow = wasAdd && openAfterAdd && currentCount() > 0;
    if (openNow) {
      openCart();
      cart.classList.add('is-busy');
    }

    Promise.resolve(event.promise)
      .then((result) => {
        if (result && result.detail && result.detail.didError) {
          if (openNow) closeCart();
          return;
        }
        const newCount = result && result.cart ? result.cart.item_count : undefined;
        return refreshCart(wasAdd, newCount).then(() => {
          if (wasAdd && openAfterAdd) openCart();
        });
      })
      .catch(() => {})
      .finally(() => cart.classList.remove('is-busy'));
  });

  /* ------------------------------------------------------------------------
     7. OLDER SHORTCUTS (used by Dub sections)
     ------------------------------------------------------------------------ */
  document.addEventListener('dub:cart:added', () => refreshCart(true));
  window.dubOpenCart = function () {
    refreshCart(false);
    openCart();
  };

  /* ------------------------------------------------------------------------
     8. BACK BUTTON
     If the browser shows this page from its memory (after pressing Back),
     the cart might be out of date, so fetch the latest.
     ------------------------------------------------------------------------ */
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refreshCart(false);
  });
})();